import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PolarArea } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Carousel, IconButton } from "@material-tailwind/react";
import NewWindow from "react-new-window";
import PDFWindowContent from "./PDFGenerator";

const imageUrl = process.env.REACT_APP_IMAGE_URL;

const ManagerDetails = ({
  selectedManager,
  categoriesData,
  generateManagerSummary,
  notify,
  onPDFHandlerReady,
}) => {
  const { t } = useTranslation();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [feedbackList, setFeedbackList] = useState([]);
  const [view, setView] = useState("user");

  // AI Summary state
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  // Skills modal state
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [selectedCategoryForSkills, setSelectedCategoryForSkills] =
    useState(null);
  const [skillsData, setSkillsData] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showPDFWindow, setShowPDFWindow] = useState(false);

  // Memoize PDF options
  const pdfOptions = useMemo(
    () => ({
      filename: selectedManager
        ? `${selectedManager.manager.first_name}_${selectedManager.manager.last_name}_survey_results.pdf`
        : "survey_results.pdf",
      method: "save",
      resolution: 2, // Higher resolution for better quality
      page: {
        format: "a4",
        orientation: "landscape",
        margin: 0,
      },
      canvas: {
        mimeType: "image/jpeg",
        qualityRatio: 0.98,
      },
      overrides: {
        pdf: {
          compress: true,
        },
        canvas: {
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
        },
      },
    }),
    [selectedManager]
  );

  const piePalette = useMemo(
    () => [
      {
        name: "light-green",
        backgroundColor: "rgba(216, 240, 232, 0.7)",
        selectedCategoryBackgroundColor: "rgba(200, 220, 210, 1)",
      },
      {
        name: "light-blue",
        backgroundColor: "rgba(222, 238, 249, 0.7)",
        selectedCategoryBackgroundColor: "rgba(200, 220, 240, 1)",
      },
      {
        name: "light-brown",
        backgroundColor: "rgba(252, 244, 228, 0.9)",
        selectedCategoryBackgroundColor: "rgba(230, 220, 200, 1)",
      },
      {
        name: "dark-purple",
        backgroundColor: "rgba(229, 227, 241, 0.8)",
        selectedCategoryBackgroundColor: "rgba(210, 200, 230, 1)",
      },
      {
        name: "light-purple",
        backgroundColor: "rgba(237, 228, 246, 0.8)",
        selectedCategoryBackgroundColor: "rgba(220, 210, 230, 1)",
      },
      {
        name: "dark-red",
        backgroundColor: "rgba(251, 230, 237, 0.8)",
        selectedCategoryBackgroundColor: "rgba(230, 200, 210, 1)",
      },
      {
        name: "hot-orange",
        backgroundColor: "rgba(253, 126, 20, 0.7)",
        selectedCategoryBackgroundColor: "rgba(253, 126, 20, 1)",
      },
      {
        name: "sea-blue",
        backgroundColor: "rgba(0, 123, 255, 0.7)",
        selectedCategoryBackgroundColor: "rgba(0, 123, 255, 1)",
      },
    ],
    []
  );

  let radarData = useMemo(
    () => ({
      labels: categoriesData?.map(({ title }) => title),
      datasets: [
        {
          data: categoriesData?.map(
            ({ title }) => selectedManager.category_scores[title]?.latest_score
          ),
          backgroundColor: categoriesData?.map(({ palette, title }) =>
            selectedCategory === title
              ? piePalette.find((p) => p.name === palette)
                  .selectedCategoryBackgroundColor
              : piePalette.find((p) => p.name === palette).backgroundColor
          ),
          borderWidth: 0,
          circular: true,
          borderJoinStyle: "round",
          drawOnChartArea: false,
        },
      ],
    }),
    [categoriesData, selectedManager, piePalette, selectedCategory]
  );

  const getCategoryData = (category) => {
    return categoriesData.find((cat) => cat.title === category);
  };

  const calculateSkillsData = useCallback((category, managerData) => {
    if (!category || !category.skills || !managerData) return null;

    const skills = category.skills.map((skill) => {
      let totalRating = 0;
      let totalResponses = 0;
      if (managerData.all_surveys && managerData.all_surveys.length > 0) {
        managerData.all_surveys.forEach((survey) => {
          if (survey.data && survey.data.categories) {
            const surveyCategory = survey.data.categories.find(
              (cat) => cat.title === category.title
            );
            if (surveyCategory && surveyCategory.skills) {
              const surveySkill = surveyCategory.skills.find(
                (s) => s.title === skill.title
              );
              if (surveySkill && surveySkill.questions) {
                surveySkill.questions.forEach((question) => {
                  if (question.answer && question.answer.rating !== undefined) {
                    totalRating += question.answer.rating;
                    totalResponses += question.answer.possible_score_range || 6; // Default to 6 if not specified
                  }
                });
              }
            }
          }
        });
      }

      let average = 0;
      if (totalResponses > 0) {
        average = (((totalRating || 0) / (totalResponses || 1)) * 6).toFixed(1);
        average = parseFloat(average);
      }

      return {
        title: skill.title,
        text: skill.text,
        average: average || 0,
        palette: category.palette,
      };
    });

    return {
      categoryTitle: category.title,
      categoryPalette: category.palette,
      skills: skills,
    };
  }, []);

  // AI Summary functions
  const [aiSummary, setAiSummary] = useState(null); // null = no summary, object = parsed JSON

  // Initialize summary from selectedManager data
  useEffect(() => {
    if (selectedManager?.manager?.manager_summary) {
      try {
        // Try to parse as JSON
        const parsed = JSON.parse(selectedManager.manager.manager_summary);
        setAiSummary(parsed);
      } catch (e) {
        // If not JSON, treat as old format (string) - but we're not supporting this anymore
        setAiSummary(null);
      }
    } else {
      setAiSummary(null);
    }
  }, [selectedManager?.manager?.manager_summary]);

  const handleSummaryAction = async () => {
    if (!selectedManager?.manager?.id) return;

    setIsGeneratingSummary(true);
    try {
      const response = await generateManagerSummary(selectedManager.manager.id);

      if (response?.success) {
        // Update local state with the new summary (response.summary is the full object)
        if (response.summary) {
          setAiSummary(response.summary);
        }
        // Show the translated message from the backend
        const message = response?.messageKey
          ? t(response.messageKey, response?.messageKeyParams || {})
          : response?.message ||
            t("analyze.managers.summary_generated_success");
        notify(message, "success");
      } else {
        // Handle case where response exists but success is false
        const errorMessage =
          response?.message ||
          t("analyze.managers.summary_generation_failed") ||
          "Failed to generate manager summary";
        notify(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        t("analyze.managers.summary_generation_failed") ||
        "Failed to generate manager summary";
      notify(errorMessage, "error");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handlePieChartClick = useCallback(
    (event, elements) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        const category = categoriesData?.[clickedIndex];

        if (category && category.skills && category.skills.length > 0) {
          const skillsData = calculateSkillsData(category, selectedManager);

          if (skillsData) {
            setSelectedCategoryForSkills(category);
            setSkillsData(skillsData);
            setSelectedSkill(skillsData.skills[0]?.title || null); // Preselect first skill
            setShowSkillsModal(true);
          }
        }
      }
    },
    [categoriesData, selectedManager, calculateSkillsData]
  );

  const handleSkillClick = useCallback((skillTitle) => {
    setSelectedSkill(skillTitle);
  }, []);

  const handleCloseSkillsModal = useCallback(() => {
    setShowSkillsModal(false);
    setSelectedCategoryForSkills(null);
    setSkillsData(null);
    setSelectedSkill(null);
  }, []);

  const handleCategoryDoubleClick = useCallback(
    (categoryTitle) => {
      const category = categoriesData?.find(
        (cat) => cat.title === categoryTitle
      );
      if (category && category.skills && category.skills.length > 0) {
        const skillsData = calculateSkillsData(category, selectedManager);
        if (skillsData) {
          setSelectedCategoryForSkills(category);
          setSkillsData(skillsData);
          setSelectedSkill(skillsData.skills[0]?.title || null);
          setShowSkillsModal(true);
        }
      }
    },
    [categoriesData, selectedManager, calculateSkillsData]
  );

  const handleDownloadPDF = useCallback(() => {
    if (!selectedManager) {
      return;
    }
    // Open new window for PDF generation (like PNG export)
    setIsGeneratingPDF(true);
    setShowPDFWindow(true);
  }, [selectedManager]);

  // Handle PDF window close
  const handlePDFWindowClose = useCallback(() => {
    setShowPDFWindow(false);
    setIsGeneratingPDF(false);
  }, []);

  // Expose PDF handler to parent
  useEffect(() => {
    if (onPDFHandlerReady) {
      onPDFHandlerReady({
        handleDownloadPDF,
        isGeneratingPDF,
      });
    }
  }, [handleDownloadPDF, isGeneratingPDF, onPDFHandlerReady]);

  useEffect(() => {
    if (
      !selectedManager ||
      !categoriesData?.length ||
      !selectedManager?.all_surveys?.length
    ) {
      return;
    }

    setSelectedCategory(categoriesData[0].title);

    const allSurveys = selectedManager.all_surveys;

    if (!allSurveys.length) {
      return;
    }

    // First, collect all unique questions from all surveys
    const allQuestions = new Map();
    allSurveys.forEach((survey) => {
      const questions = survey?.data?.inputs?.questions || [];
      questions.forEach((question) => {
        // Store question by its text (category) to ensure uniqueness
        if (!allQuestions.has(question.text)) {
          allQuestions.set(question.text, {
            text: question.text,
            id: question.id,
          });
        }
      });
    });

    // Then collect all answers for each question
    const dataFeedback = allSurveys.flatMap((survey) => {
      const questions = survey?.data?.inputs?.questions || [];
      return questions.flatMap((question) =>
        (question.answers || []).map((answer) => ({
          type: answer.type,
          category: question.text,
          response: answer.response,
        }))
      );
    });

    // Ensure all questions are represented, even if they have no answers
    const questionsWithAnswers = new Set(dataFeedback.map((fb) => fb.category));
    allQuestions.forEach((question) => {
      if (!questionsWithAnswers.has(question.text)) {
        // Add empty entry for questions without answers
        dataFeedback.push({
          type: "user", // default type
          category: question.text,
          response: null, // no response yet
        });
      }
    });

    setFeedbackList(dataFeedback);
  }, [selectedManager, categoriesData]);

  const categoriesLength = categoriesData?.length || 0;
  const midpoint = Math.ceil(categoriesLength / 2);

  const leftSideData = (categoriesData || []).slice(
    0,
    midpoint - (categoriesLength % 2 === 0 ? 0 : 1)
  );
  const rightSideData = (categoriesData || []).slice(
    midpoint - (categoriesLength % 2 === 0 ? 0 : 1)
  );

  // Get all unique question categories from the survey data (not just from answers)
  const allQuestionCategories = new Set();
  if (selectedManager?.all_surveys?.length > 0) {
    selectedManager.all_surveys.forEach((survey) => {
      const questions = survey?.data?.inputs?.questions || [];
      questions.forEach((question) => {
        allQuestionCategories.add(question.text);
      });
    });
  }

  // Filter and categorize feedback by view type
  const feedbackByCategory = feedbackList
    .filter(
      (feedback) => feedback?.type === view && feedback?.response !== null
    )
    .reduce((categoryMap, { response, category }) => {
      if (!categoryMap[category]) {
        categoryMap[category] = [];
      }
      categoryMap[category].push(response);
      return categoryMap;
    }, {});

  // Build complete array with all questions, including those without answers
  const filteredAndCategorizedFeedback = Array.from(allQuestionCategories).map(
    (category) => [category, feedbackByCategory[category] || []]
  );

  return (
    <>
      {categoriesData?.length > 0 && (
        <div key={selectedManager?.manager.id}>
          {/* Page 1: Main Dashboard */}
          <div>
            <div className="lg:flex w-full">
              <div className="box-white lg:max-w-full lg:min-w-[66%] !rounded-b-none">
                <h3 className="title-primary-md mb-1">
                  {selectedManager.manager.first_name}{" "}
                  {selectedManager.manager.last_name}
                </h3>
                <p className="text-[#686285] w-full mb-6">
                  {selectedManager.manager.email}
                </p>
                <div className="lg:flex items-center">
                  <div className="lg:w-1/3 lg:pr-3 lg:mb-10 mb-4">
                    {leftSideData.map(({ title, palette }) => (
                      <button
                        key={title}
                        className={`graph-btn ${
                          title !== selectedCategory
                            ? "text-"
                            : "text-white bg-"
                        }${palette}`}
                        onClick={() => setSelectedCategory(title)}
                        onDoubleClick={() => handleCategoryDoubleClick(title)}
                      >
                        <span className="flex-1">{title}</span>
                        <span className="ml-2 whitespace-nowrap">
                          <span>{`${selectedManager.category_scores[title]?.latest_score}`}</span>{" "}
                          {selectedManager.category_scores[title]
                            ?.self_score && (
                            <span className="font-light">
                              {`(${t("anaylze.managers.self_assessed")}: ${
                                selectedManager.category_scores[title]
                                  ?.self_score
                              })`}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="lg:w-1/3 lg:px-3 lg:mb-10 mb-4">
                    <PolarArea
                      className="mx-auto p-2 !w-[200px] !h-[200px] cursor-pointer"
                      data={radarData}
                      options={{
                        animation: { duration: 1400 },
                        onClick: handlePieChartClick,
                        plugins: {
                          ChartDataLabels,
                          datalabels: {
                            formatter: function (value, context) {
                              return "";
                            },
                            anchor: "end",
                            align: "center",
                            font: {
                              family: "Poppins, sans-serif",
                              weight: "bold",
                            },
                          },
                          legend: {
                            display: false,
                          },
                        },
                        clip: { left: 0, top: 0, right: 0, bottom: 0 },
                        scales: {
                          r: {
                            backgroundColor: "#F3F3FA",
                            drawOnChartArea: false,
                            min: 0,
                            max: 6,
                            angleLines: {
                              display: true,
                              color: "white",
                              lineWidth: 2,
                            },
                            grid: {
                              color: "white",
                            },
                            ticks: {
                              maxTicksLimit: 1,
                              display: false,
                            },
                            pointLabels: {
                              display: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>
                  <div className="lg:w-1/3 lg:pl-3 lg:mb-10 mb-4">
                    {rightSideData.map(({ title, palette }) => (
                      <button
                        key={title}
                        className={`graph-btn ${
                          title !== selectedCategory
                            ? "text-"
                            : "text-white bg-"
                        }${palette}`}
                        onClick={() => setSelectedCategory(title)}
                        onDoubleClick={() => handleCategoryDoubleClick(title)}
                      >
                        <span className="flex-1">{title}</span>
                        <span className="ml-2 whitespace-nowrap">
                          <span>{`${selectedManager.category_scores[title]?.latest_score}`}</span>{" "}
                          {selectedManager.category_scores[title]
                            ?.self_score && (
                            <span className="font-light">
                              {`(${t("anaylze.managers.self_assessed")}: ${
                                selectedManager.category_scores[title]
                                  ?.self_score
                              })`}
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {selectedCategory && (
                  <div className="lg:flex lg:flex-wrap">
                    <div className="lg:w-1/2 w-full lg:pr-6">
                      <h3 className="title-primary-base mb-5">
                        {t("analyze.managers.cat_description_title")}
                      </h3>

                      <div
                        className={`survey-box !bg-transparent !m-0 !p-0 w-full element-${
                          getCategoryData(selectedCategory)?.palette
                        }`}
                      >
                        <span className="survey-image !h-auto">
                          <img
                            src={
                              imageUrl +
                              (getCategoryData(selectedCategory)?.icon?.url ||
                                "")
                            }
                            alt={selectedCategory}
                          />
                          <h3 className="title-md text-center mt-3">
                            {selectedCategory}
                          </h3>
                        </span>
                        <div className="inline-block align-middle lg:max-w-[calc(100%-125px)] max-w-[calc(100%-85px)]">
                          <h4
                            className={`title-[14px] font-bold mb-4 text-${
                              getCategoryData(selectedCategory)?.palette
                            }`}
                          >
                            {getCategoryData(selectedCategory)?.text}
                          </h4>
                          <p className="text-[#686285] leading-[18px]">
                            {getCategoryData(selectedCategory)?.summary}
                          </p>
                        </div>
                      </div>
                    </div>
                    {getCategoryData(selectedCategory)?.skills?.length > 0 && (
                      <div className="lg:w-1/2 w-full lg:pl-6">
                        <h3 className="title-primary-base mb-5">
                          {t("analyze.managers.questions_title")}
                        </h3>
                        <Carousel
                          className={`rounded-xl bg-${
                            getCategoryData(selectedCategory).palette
                          }-light h-auto text-${
                            getCategoryData(selectedCategory).palette
                          }`}
                          prevArrow={({ handlePrev }) => (
                            <IconButton
                              variant="text"
                              onClick={handlePrev}
                              className={`!absolute top-2/4 -translate-y-2/4 left-2 w-4 h-4 hover:bg-transparent text-${
                                getCategoryData(selectedCategory).palette
                              }`}
                            >
                              <ChevronLeftIcon className="w-4 h-4" />
                            </IconButton>
                          )}
                          nextArrow={({ handleNext }) => (
                            <IconButton
                              variant="text"
                              onClick={handleNext}
                              className={`!absolute top-2/4 -translate-y-2/4 right-2 w-4 h-4 hover:bg-transparent text-${
                                getCategoryData(selectedCategory)?.palette
                              }`}
                            >
                              <ChevronRightIcon className="w-4 h-4" />
                            </IconButton>
                          )}
                        >
                          {getCategoryData(selectedCategory)?.skills?.length >
                            0 &&
                            getCategoryData(selectedCategory)?.skills?.map(
                              (skill) =>
                                skill?.questions.length > 0 &&
                                skill?.questions.map((question) => (
                                  <div className="p-8 pb-12">
                                    {question.text}
                                  </div>
                                ))
                            )}
                        </Carousel>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Qualitative Feedback - Keep in same container for UI, separate for PDF */}
          {selectedCategory && (
            <div className={view === "summary" ? "w-full" : "lg:flex w-full"}>
              <div
                className={
                  view === "summary"
                    ? "box-white w-full !rounded-t-none"
                    : "box-white lg:max-w-full lg:min-w-[66%] !rounded-t-none"
                }
              >
                <div className="w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="title-primary-base">
                      {t("analyze.company.feedback_title")}
                    </h3>
                    <div className="flex space-x-4 mr-4">
                      <button
                        className={`graph-btn ${
                          view === "summary"
                            ? "text-white bg-dark-purple"
                            : "text-dark-purple"
                        }`}
                        onClick={() => setView("summary")}
                      >
                        {t("analyze.managers.summary")}
                        <span className="ml-2 px-2 py-1 text-xs font-semibold text-dark-purple bg-white rounded-full">
                          Beta
                        </span>
                      </button>
                      <button
                        className={`graph-btn ${
                          view === "user"
                            ? "text-white bg-dark-purple"
                            : "text-dark-purple"
                        }`}
                        onClick={() => setView("user")}
                      >
                        {t("analyze.managers.list")}
                      </button>
                    </div>
                  </div>
                  <div className="mt-6">
                    {view === "summary" ? (
                      // Manager Summary View
                      aiSummary &&
                      aiSummary.questions &&
                      aiSummary.questions.length > 0 ? (
                        <div className="space-y-6">
                          {aiSummary.questions.map((question, index) => (
                            <div
                              key={question.questionId || index}
                              className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285]"
                            >
                              <h4 className="title-primary-base mb-4">
                                {question.questionText}
                              </h4>

                              <div className="mb-4">
                                <strong className="text-[#686285]">
                                  {t("analyze.managers.summary_header") ||
                                    "Zusammenfassung der Rückmeldungen"}
                                </strong>
                                <div className="whitespace-pre-wrap text-sm leading-relaxed mt-2">
                                  {question.summary}
                                </div>
                              </div>

                              <p className="text-sm italic mb-4">
                                {t("analyze.managers.count_statement", {
                                  count: question.count,
                                })}
                              </p>

                              <div className="mt-4">
                                <strong className="text-[#686285]">
                                  {t(
                                    "analyze.managers.recommendation_header"
                                  ) || "Top-Empfehlung:"}
                                </strong>
                                <div className="whitespace-pre-wrap text-sm leading-relaxed mt-2">
                                  {question.recommendation}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285] text-center py-8">
                          <p className="text-gray-500 mb-4">
                            {t("analyze.managers.no_results")}
                          </p>
                          <button
                            onClick={handleSummaryAction}
                            disabled={isGeneratingSummary}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                          >
                            {isGeneratingSummary
                              ? t("surveyResults.feedback.generating_summary")
                              : t("surveyResults.feedback.generate_summary")}
                          </button>
                        </div>
                      )
                    ) : (
                      // List View - Original feedback cards
                      <div className="flex mt-6 flex-wrap">
                        {filteredAndCategorizedFeedback.length > 0 ? (
                          filteredAndCategorizedFeedback.map(
                            ([category, feedbackArray]) => (
                              <div
                                style={{
                                  width: `${
                                    100 / filteredAndCategorizedFeedback.length
                                  }%`,
                                }}
                                key={category}
                              >
                                <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285] mr-4 mb-4">
                                  <h4 className="title-primary-base mb-3">
                                    {category}
                                  </h4>
                                  <div className="space-y-3">
                                    {feedbackArray.length > 0 ? (
                                      feedbackArray.map((response, index) => (
                                        <div
                                          key={index}
                                          className="flex justify-between"
                                        >
                                          <div>{response}</div>
                                          <div className="text-xs ml-2">
                                            {index + 1}/{feedbackArray.length}
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-[#999] italic">
                                        {t("analyze.managers.no_results")}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          )
                        ) : (
                          <div className="w-full">
                            <div className="rounded-[22px] bg-[#F3F3FA] p-7 text-[#686285] mr-4 mb-4">
                              <h4 className="title-primary-base">
                                {t("analyze.managers.no_results")}
                              </h4>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PDF Window - opens in new window like PNG export */}
      {showPDFWindow && (
        <NewWindow
          features={{ width: 1400, height: 700 }}
          onUnload={() => {
            setShowPDFWindow(false);
            setIsGeneratingPDF(false);
          }}
        >
          <PDFWindowContent
            categoriesData={categoriesData}
            selectedManager={selectedManager}
            calculateSkillsData={calculateSkillsData}
            piePalette={piePalette}
            leftSideData={leftSideData}
            rightSideData={rightSideData}
            radarData={radarData}
            filteredAndCategorizedFeedback={filteredAndCategorizedFeedback}
            managerSummary={aiSummary}
            t={t}
            pdfOptions={pdfOptions}
            onClose={handlePDFWindowClose}
          />
        </NewWindow>
      )}

      {/* Skills Modal */}
      {showSkillsModal && skillsData && (
        <div
          className="modal animate-in fade-in duration-300"
          style={{ zIndex: 9999 }}
        >
          <div
            className="overlay animate-in fade-in duration-300"
            onClick={handleCloseSkillsModal}
          ></div>
          <div
            className="modal-body animate-in zoom-in-95 duration-300"
            style={{ zIndex: 10000, maxWidth: "800px" }}
          >
            <button className="close-button" onClick={handleCloseSkillsModal}>
              X
            </button>

            <div className="modal-overflow">
              <div className="modal-inner">
                <div className="w-full p-6">
                  <h3 className="title-primary-md mb-3 text-center text-lg">
                    {selectedCategoryForSkills?.title} - Skills Breakdown
                  </h3>
                  <div className="lg:flex items-center">
                    {(() => {
                      const categoryPalette = piePalette.find(
                        (p) => p.name === selectedCategoryForSkills?.palette
                      );
                      return (
                        <>
                          {/* Skills List - Left Side */}
                          <div className="lg:w-1/2 lg:pr-2 lg:mb-4 mb-3 flex flex-col justify-center">
                            {skillsData.skills.map((skill, index) => {
                              const isSelected = selectedSkill === skill.title;

                              return (
                                <button
                                  key={skill.title}
                                  onClick={() => handleSkillClick(skill.title)}
                                  className={`graph-btn transition-all duration-200 hover:scale-[1.02] whitespace-nowrap min-h-[48px] ${
                                    !isSelected ? "text-" : "text-white bg-"
                                  }${selectedCategoryForSkills?.palette} ${
                                    isSelected
                                      ? "ring-2 ring-white ring-opacity-50 shadow-lg"
                                      : ""
                                  }`}
                                >
                                  {skill.title}
                                  <span className="float-right">
                                    <span>
                                      {(skill.average || 0).toFixed(1)}
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Chart - Right Side */}
                          <div className="lg:w-1/2 lg:pl-2 lg:mb-4 mb-3 flex flex-col items-center justify-center">
                            <PolarArea
                              className="mx-auto !w-[250px] !h-[250px] transition-all duration-300"
                              data={{
                                labels: skillsData.skills.map(
                                  (skill) => skill.title
                                ),
                                datasets: [
                                  {
                                    data: skillsData.skills.map(
                                      (skill) => skill.average
                                    ),
                                    backgroundColor: skillsData.skills.map(
                                      (skill, index) => {
                                        const isSelected =
                                          selectedSkill === skill.title;

                                        if (isSelected) {
                                          return (
                                            categoryPalette?.selectedCategoryBackgroundColor ||
                                            "#3B82F6"
                                          );
                                        } else {
                                          const baseColor =
                                            categoryPalette?.backgroundColor ||
                                            "#3B82F6";
                                          const opacity = 0.4 + index * 0.2;
                                          return baseColor.replace(
                                            /[\d.]+\)$/g,
                                            `${opacity})`
                                          );
                                        }
                                      }
                                    ),
                                    borderColor: skillsData.skills.map(
                                      (skill, index) => {
                                        const isSelected =
                                          selectedSkill === skill.title;
                                        return isSelected
                                          ? categoryPalette?.selectedCategoryBackgroundColor ||
                                              "#1E40AF"
                                          : categoryPalette?.backgroundColor ||
                                              "#3B82F6";
                                      }
                                    ),
                                    borderWidth: 3,
                                  },
                                ],
                              }}
                              options={{
                                animation: { duration: 1400 },
                                plugins: {
                                  ChartDataLabels,
                                  datalabels: {
                                    display: false,
                                  },
                                  legend: {
                                    display: false,
                                  },
                                },
                                clip: false,
                                layout: {
                                  padding: 15,
                                },
                                scales: {
                                  r: {
                                    backgroundColor: "#F3F3FA",
                                    drawOnChartArea: false,
                                    min: 0,
                                    max: 6,
                                    angleLines: {
                                      display: true,
                                      color: "white",
                                      lineWidth: 2,
                                    },
                                    grid: {
                                      color: "white",
                                    },
                                    ticks: {
                                      maxTicksLimit: 1,
                                      display: false,
                                    },
                                    pointLabels: {
                                      display: false,
                                    },
                                  },
                                },
                                maintainAspectRatio: true,
                                responsive: true,
                              }}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerDetails;
